// src/utils/formatLocale.js
//
// Single source of truth for "what BCP-47 locale tag should I pass to
// Intl / toLocaleString right now?" Reads i18next's active language
// and maps it to a sensible regional tag.
//
// Why not just pass i18n.language directly? Because i18n.language is
// 'en' or 'es' (language only — load: 'languageOnly' in i18n config),
// but Intl prefers a regional tag like 'en-US' or 'es-MX' for correct
// formatting. The map below picks the regional default that best
// matches our user demographic:
//   - en → en-US (US-based coaches and English-speaking parents)
//   - es → es-MX (US-based Spanish-speaking parents are predominantly
//          Mexican / Central American; Mexican Spanish gives the most
//          natural "8 abr." instead of European "8 de abr.")
//
// Stays a plain function (not a hook) so it's safe to use inside
// pure helpers like fmtTime() and fmtDayHeader() that aren't React
// components. Reads i18n's language directly each call — this is
// cheap, and means the formatters always reflect the latest toggle
// without needing a re-render dependency.
//
// USAGE: replace any hardcoded 'en-US' in toLocale*String calls with
// activeLocale(). For currency formatters specifically, prefer staying
// on 'en-US' since US-based Hispanic parents read prices in US format
// ($1,234.56), not Mexican format ($1.234,56) — this util is for
// dates and times only.

import i18n from '@/i18n';

const LOCALE_MAP = {
  en: 'en-US',
  es: 'es-MX',
};

/**
 * Current BCP-47 locale tag based on the active i18next language.
 * Falls back to 'en-US' if the language is missing or unrecognized.
 */
export function activeLocale() {
  const lang = String(i18n?.language || 'en').toLowerCase().split('-')[0];
  return LOCALE_MAP[lang] || 'en-US';
}

export default activeLocale;
